/**
 * Particle Field Background Animation
 * Tiny floating particles that create depth with occasional connecting lines
 */

import gsap from 'gsap'

interface Particle {
  x: number
  y: number
  vx: number
  vy: number
  size: number
  opacity: number
  color: string
}

export function initParticleField() {
  const container = document.getElementById('heroOrbs')
  if (!container) return

  // Create canvas for particles
  const canvas = document.createElement('canvas')
  canvas.style.cssText = `
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
  `

  const ctx = canvas.getContext('2d')
  if (!ctx) return

  container.appendChild(canvas)

  // Brand colors for particles
  const colors = [
    '#47DBFF', // Cyan
    '#3ADFD8', // Teal
    '#EC6696', // Magenta
    '#325EFC', // Blue
  ]

  // Create particles array first
  const particleCount = 80
  const particles: Particle[] = []

  // Set canvas size
  let previousWidth = container.offsetWidth
  let previousHeight = container.offsetHeight

  const resizeCanvas = () => {
    const newWidth = container.offsetWidth
    const newHeight = container.offsetHeight

    // Scale particle positions proportionally (only if particles exist)
    if (particles.length > 0) {
      const scaleX = newWidth / previousWidth
      const scaleY = newHeight / previousHeight

      particles.forEach((particle) => {
        particle.x *= scaleX
        particle.y *= scaleY
      })
    }

    canvas.width = newWidth
    canvas.height = newHeight

    previousWidth = newWidth
    previousHeight = newHeight
  }

  // Initial canvas size setup
  canvas.width = container.offsetWidth
  canvas.height = container.offsetHeight

  window.addEventListener('resize', resizeCanvas)

  for (let i = 0; i < particleCount; i++) {
    particles.push({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      vx: (Math.random() - 0.5) * 0.3, // Slow horizontal velocity
      vy: (Math.random() - 0.5) * 0.3, // Slow vertical velocity
      size: 1 + Math.random() * 4, // 1-5px
      opacity: 0.3 + Math.random() * 0.4, // 0.3-0.7
      color: colors[Math.floor(Math.random() * colors.length)],
    })
  }

  // Connection distance threshold
  const connectionDistance = 150

  // Animation loop
  function draw() {
    if (!ctx || !container) return

    const width = canvas.width
    const height = canvas.height

    // Clear canvas
    ctx.clearRect(0, 0, width, height)

    // Update and draw particles
    particles.forEach((particle) => {
      // Update position
      particle.x += particle.vx
      particle.y += particle.vy

      // Wrap around edges
      if (particle.x < 0) particle.x = width
      if (particle.x > width) particle.x = 0
      if (particle.y < 0) particle.y = height
      if (particle.y > height) particle.y = 0

      // Draw particle
      ctx.beginPath()
      ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2)
      ctx.fillStyle = particle.color
      ctx.globalAlpha = particle.opacity
      ctx.fill()
    })

    // Draw connections between nearby particles
    ctx.globalAlpha = 0.25

    for (let i = 0; i < particles.length; i++) {
      for (let j = i + 1; j < particles.length; j++) {
        const dx = particles[i].x - particles[j].x
        const dy = particles[i].y - particles[j].y
        const distance = Math.sqrt(dx * dx + dy * dy)

        if (distance < connectionDistance) {
          // Generate consistent random thickness for this particle pair
          const seed = ((i * 7919 + j * 7907) % 10000) / 10000
          const thickness = 0.5 + seed * 0.75 // 0.5 to 1.25
          ctx.lineWidth = thickness

          // Fade connection based on distance
          const opacity = (1 - distance / connectionDistance) * 0.35
          ctx.globalAlpha = opacity

          ctx.beginPath()
          ctx.moveTo(particles[i].x, particles[i].y)
          ctx.lineTo(particles[j].x, particles[j].y)
          ctx.strokeStyle = particles[i].color
          ctx.stroke()
        }
      }
    }

    ctx.globalAlpha = 1

    requestAnimationFrame(draw)
  }

  draw()

  // Add subtle pulsing animation to random particles
  particles.forEach((particle) => {
    gsap.to(particle, {
      opacity: particle.opacity * 1.5,
      duration: 2 + Math.random() * 3,
      repeat: -1,
      yoyo: true,
      ease: 'sine.inOut',
    })
  })
}
